/**
 * Auth Context - Global Authentication State
 * Production-ready state management for auth
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI, tokenManager, isAuthenticated as checkAuth } from '../services/api';
import { clearAllViews } from '../services/viewCache';

// Context
const AuthContext = createContext(null);

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        if (checkAuth()) {
          // Try to get user from storage first
          const storedUser = tokenManager.getUser();
          if (storedUser) {
            setUser(storedUser);
          }
          
          // Then verify with server
          try {
            const response = await authAPI.getProfile();
            if (response.success && response.data?.user) {
              setUser(response.data.user);
            }
          } catch (err) {
            // Token invalid - clear auth
            if (err.status === 401) {
              tokenManager.clearAll();
              setUser(null);
            }
          }
        }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for logout events from API service
    const handleLogout = (event) => {
      setUser(null);
      clearAllViews(); // don't leak the prior session's cached lists to the next user
      const errorMessage = event.detail?.message || 'Session expired';
      setError(errorMessage);
    };
    
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Login - sends OTP
  const login = useCallback(async (mobile) => {
    setError(null);
    try {
      const response = await authAPI.login(mobile);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Password login (email/mobile + password, no OTP)
  const passwordLogin = useCallback(async (identifier, password, rememberMe = false) => {
    setError(null);
    try {
      const response = await authAPI.passwordLogin(identifier, password, rememberMe);
      if (response.success && response.data?.user) {
        setUser(response.data.user);
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Register
  const register = useCallback(async (userData) => {
    setError(null);
    try {
      const response = await authAPI.register(userData);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Verify OTP
  const verifyOTP = useCallback(async (mobile, otp) => {
    setError(null);
    try {
      const response = await authAPI.verifyOTP(mobile, otp);
      if (response.success && response.data?.user) {
        setUser(response.data.user);
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Resend OTP
  const resendOTP = useCallback(async (mobile) => {
    setError(null);
    try {
      const response = await authAPI.resendOTP(mobile);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Verify the email OTP that was sent as step 2 of password-login 2FA.
  // On success, tokens are stored by authAPI and user state is set here.
  const verifyEmailOtp = useCallback(async (identifier, otp) => {
    setError(null);
    try {
      const response = await authAPI.verifyEmailOtp(identifier, otp);
      if (response.success && response.data?.user) {
        setUser(response.data.user);
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Resend the email OTP. Anti-enumeration generic response from server.
  const resendEmailOtp = useCallback(async (identifier) => {
    setError(null);
    try {
      const response = await authAPI.resendEmailOtp(identifier);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Magic-link consume — called by the /magic-login interstitial.
  const magicLogin = useCallback(async (token, rememberMe = false) => {
    setError(null);
    try {
      const response = await authAPI.magicLogin(token, rememberMe);
      if (response.success && response.data?.user) {
        setUser(response.data.user);
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Update profile
  const updateProfile = useCallback(async (data) => {
    setError(null);
    try {
      const response = await authAPI.updateProfile(data);
      if (response.success && response.data?.user) {
        setUser(response.data.user);
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Change password (requires current password)
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    setError(null);
    try {
      return await authAPI.changePassword(currentPassword, newPassword);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setError(null);
      clearAllViews();
    }
  }, []);

  // Logout from all devices
  const logoutAll = useCallback(async () => {
    try {
      await authAPI.logoutAll();
    } catch (err) {
      console.error('Logout all error:', err);
    } finally {
      setUser(null);
      setError(null);
      clearAllViews();
    }
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    if (!checkAuth()) return;
    
    try {
      const response = await authAPI.getProfile();
      if (response.success && response.data?.user) {
        setUser(response.data.user);
      }
    } catch (err) {
      console.error('Refresh user error:', err);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Memoized so consumers don't re-render on every provider render (a fresh
  // object literal would invalidate every useContext consumer each time).
  const value = useMemo(() => ({
    // State
    user,
    loading,
    error,
    isAuthenticated: !!user,
    setUser,

    // Actions
    login,
    passwordLogin,
    register,
    verifyOTP,
    resendOTP,
    verifyEmailOtp,
    resendEmailOtp,
    magicLogin,
    updateProfile,
    changePassword,
    logout,
    logoutAll,
    refreshUser,
    clearError,
  }), [
    user, loading, error, setUser,
    login, passwordLogin, register, verifyOTP, resendOTP, verifyEmailOtp,
    resendEmailOtp, magicLogin, updateProfile, changePassword, logout, logoutAll, refreshUser, clearError,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;








